import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { bootstrapAppSession } from "../helper/appBootstrap";
import { getCachedAppSettings, isSettingsCacheReady } from "../helper/settingsCache";
import { getCachedWidgets, isWidgetsCacheReady, setCachedWidgets } from "../helper/widgetApi";
import { loadWidgets } from "../utils/widgetLocalStorage";
import { useEnsureInstall } from "../hooks/useEnsureInstall";

const AppSessionContext = createContext(null);

export function AppSessionProvider({ children }) {
  const { isReady: installReady } = useEnsureInstall();
  const [widgets, setWidgetsState] = useState(() => getCachedWidgets() ?? loadWidgets());
  const [settings, setSettingsState] = useState(() => getCachedAppSettings());
  const [sessionReady, setSessionReady] = useState(
    () => isWidgetsCacheReady() && isSettingsCacheReady(),
  );

  useEffect(() => {
    if (!installReady || sessionReady) return undefined;

    let cancelled = false;
    bootstrapAppSession().then(() => {
      if (cancelled) return;
      setWidgetsState(getCachedWidgets() ?? []);
      setSettingsState(getCachedAppSettings());
      setSessionReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [installReady, sessionReady]);

  const setWidgets = useCallback((next) => {
    const resolved =
      typeof next === "function"
        ? next(getCachedWidgets() ?? widgets ?? [])
        : next;
    const list = Array.isArray(resolved) ? resolved : [];
    setCachedWidgets(list);
    setWidgetsState(list);
  }, [widgets]);

  /** Sync in-memory state from cache when present — never clear the list if cache is empty. */
  const refreshFromCache = useCallback(() => {
    const cached = getCachedWidgets();
    if (Array.isArray(cached)) {
      setWidgetsState(cached);
    }
    const cachedSettings = getCachedAppSettings();
    if (cachedSettings) {
      setSettingsState(cachedSettings);
    }
  }, []);

  const value = useMemo(
    () => ({
      widgets,
      setWidgets,
      settings,
      sessionReady,
      sessionLoading: !sessionReady,
      refreshFromCache,
    }),
    [widgets, setWidgets, settings, sessionReady, refreshFromCache],
  );

  return (
    <AppSessionContext.Provider value={value}>{children}</AppSessionContext.Provider>
  );
}

export function useAppSession() {
  const ctx = useContext(AppSessionContext);
  if (!ctx) {
    throw new Error("useAppSession must be used within AppSessionProvider");
  }
  return ctx;
}
